import React, { useContext } from 'react';
import { getDaysText } from 'utils/translations/i18n';
import formatCurrency from 'utils/format-currency';
import { IModalContext, ModalContext } from 'contexts/modal';
import ClockIcon from 'components/icon/clock-icon';
import MoneyHouseIcon from 'components/icon/money-house-icon';
import OverAskingIcon from 'components/icon/over-asking-icon';
import UnderAskingIcon from 'components/icon/under-asking-icon';
import styles from './style.module.scss';
import SchemaOrg from 'components/schema-org';
import { useThemeContext } from 'contexts';
import { FeaturesContext, IFeatures } from 'contexts/features';
import { TryScrambleText } from 'utils/text-scrambler';
import { listingPageModalIds } from 'constants/test-constants';
import { ThemeNames } from 'types/themes';

import type Listing from 'data/listing';
import type { Zoopraisal as TZoopraisal } from 'utils/zoopraisal';
import { buildClassName } from 'utils/build-class-name';
import Button, { SECONDARY_THEME } from 'components/control/button';
import { calculateSellingRange } from 'utils/zoopraisal/calculate-selling-range';

interface Props {
  listing: Listing;
  topOfListingRef: React.RefObject<HTMLDivElement>;
  isPrivate: boolean;
  shouldScramble: boolean;
  zoopraisalData: TZoopraisal;
}

export default function Zoopraisal({ listing, isPrivate, shouldScramble, zoopraisalData: zoopraisal }: Props) {
  const { openModal } = useContext(ModalContext) as IModalContext;
  const isHardCodedListing = listing.addressSlug === '877-forest-glen-ave-burlington-on';
  const { features } = useContext(FeaturesContext) as IFeatures;

  const openConnectionRequest = () => openModal('connect-with-agent', { listingId: listing.id, source: 'CMA' });

  const { price, daysOnMarket } = listing;
  const { averagePrice, overAskingCount, datasetCount, averageDaysOnMarket } = zoopraisal as TZoopraisal;

  const daysOnMarketText = getDaysText(daysOnMarket);
  const averageDaysOnMarketText = getDaysText(averageDaysOnMarket);
  const estimatedTimeOnMarketRemaining = averageDaysOnMarket! - daysOnMarket! > 0 ? `${getDaysText(averageDaysOnMarket! - daysOnMarket!)}.` : 'very soon!';

  const estimatedSoldDateInFuture = averageDaysOnMarket! - daysOnMarket! > 0 ? true : false;
  const percentageSimilarSold = Math.round(overAskingCount / datasetCount * 100);
  const isOverAsking = Number(price) - (averagePrice as number) < 0;
  const differenceInPrice = formatCurrency(Math.abs(Number(price) - (averagePrice as number))) || '0';
  const expectedToGoOverAsking = percentageSimilarSold / 100 > 0.6;
  const { themeName, theme } = useThemeContext();
  const isZoocasaTenant = themeName === ThemeNames.ZOOCASA;
  const isExpTheme = themeName && !isZoocasaTenant;
  const { startRange, endRange } = calculateSellingRange(price, averagePrice);
  
  return (
    <div className={styles.component} data-testid={listingPageModalIds.zoopraisal}>
      {isZoocasaTenant ? 
        <h2>
          Home Estimate Zoopraisal<span>®</span>
        </h2>:
        <>
          <h2>
          Home Appraisal
          </h2>          
        </>
      }
      <div>
        {/* This property is estimated to sell between: START and END */}
        {startRange > 0 && endRange > 0 && !isHardCodedListing && <section className={styles['stat-row']}>
          {'This property is estimated to sell between: '}
          <b className={isPrivate ? 'blur' : ''}>{TryScrambleText(formatCurrency(startRange), shouldScramble)}</b>
          {' and '}
          <b className={isPrivate ? 'blur' : ''}>{TryScrambleText(formatCurrency(endRange), shouldScramble)}</b>
          {'.'}
        </section>
        }
        {/* Similar properties in this area sold on average for: AVERAGE_PRICE */}
        <section className={styles['stat-row']}>
          <span className={styles['grey-icon']}>
            <MoneyHouseIcon />
          </span>
          Similar properties in this area sold on average for: <b className={isPrivate ? 'blur' : ''}>{TryScrambleText(isHardCodedListing ? '$2,050,000' : formatCurrency(averagePrice!), shouldScramble)}</b>.
        </section>
        {!isHardCodedListing &&
          <>
            {expectedToGoOverAsking &&
          <section className={styles['stat-row']}>
            <span className={styles['grey-icon']}>
              {isOverAsking ? <OverAskingIcon /> : <UnderAskingIcon />}
            </span>
            <b>Expect to pay {isOverAsking ? 'over' : 'under'} asking.</b> {percentageSimilarSold}% of similar properties sold for {isOverAsking ? 'more' : 'less'} than the asking price.
          </section>
            }
            <section className={styles['stat-row']}>
              <span className={styles['grey-icon']}>
                <ClockIcon />
              </span>
          Expected to sell {estimatedSoldDateInFuture ? 'in' : ''} <b className={isPrivate ? 'blur' : ''}>{TryScrambleText(estimatedTimeOnMarketRemaining, shouldScramble)}</b> Similar properties sold in <b className={isPrivate ? 'blur' : ''}>{TryScrambleText(averageDaysOnMarketText, shouldScramble)}</b>. This home has been on {theme.name} for <b className={isPrivate ? 'blur' : ''}>{TryScrambleText(daysOnMarketText, shouldScramble)}</b>.
            </section>
          </>
        }
        <section className={buildClassName(styles.disclaimer, isExpTheme && styles['exp-theme'])}>          
          <div>
            <p className={styles['estimation-disclaimer-text']}>The estimated sale price does not take into account many variables such as recent renovations, type of appliances, school district etc. To get a more accurate estimate please contact {isZoocasaTenant ? 'a Zoocasa' : 'an eXp'} agent.</p>
            <Button className={styles['contact-button']} label="Contact An Agent" onClick={openConnectionRequest} />
          </div>
        </section>
        <section className={styles['zoocasa-link']}>Powered by <a href="https://www.zoocasa.com" target='_blank'>Zoocasa</a></section>
        { features.useFAQMarkUp &&
        <SchemaOrg itemProp="mainEntity" itemType="Question">
          <meta itemProp="name" content={`How does ${listing.seoStreet} compare to other homes selling in ${listing.neighbourhoodName ? listing.neighbourhoodName + ', ': ''}${listing.city}?`} />
          <SchemaOrg itemProp="acceptedAnswer" itemType="Answer">
            <meta itemProp="text" content={`This home is listed ${differenceInPrice} ${isOverAsking ? 'below' : 'above'} the average sold price for similar properties.`} />
          </SchemaOrg>
        </SchemaOrg>
        }
      </div>
    </div>
  );
}
